import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from 'antd';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, Text } from '@react-three/drei';
import * as THREE from 'three';
import SponsortsBar from '../../structural/screen/sponsorsBar';


const blockTypes = [
  {
    id: 1,
    name: "Bloque estándar (20x20x40 cm)",
    width: 0.2,
    height: 0.2,
    length: 0.4,
    mortarGap: 0.015,
    weight: 12
  },
  {
    id: 2,
    name: "Ladrillo común (5x10x24 cm)",
    width: 0.05,
    height: 0.1,
    length: 0.24,
    mortarGap: 0.01,
    weight: 2.5
  },
  {
    id: 3,
    name: "Bloque termoarcilla (19x19x39 cm)",
    width: 0.19,
    height: 0.19,
    length: 0.39,
    mortarGap: 0.015,
    weight: 10
  }
];

const mortarDensity = 1.6;

const Wall3D = ({ wall, block }) => 
{
  const { length, height, thickness } = wall;
  const { length: blockL, height: blockH, mortarGap } = block;

  const lengthTextRef = useRef();
  const heightTextRef = useRef();  

  useFrame(({ camera }) => {
    if (lengthTextRef.current) lengthTextRef.current.lookAt(camera.position);
    if (heightTextRef.current) heightTextRef.current.lookAt(camera.position);
  });

  const blocks = [];
  let currentY = -height/2;
  let rowCounter = 0;

  while (currentY < height/2) {
    const rowHeight = Math.min(blockH, height/2 - currentY);
    if (rowHeight <= 0) break;

    let currentX = -length/2;
    const rowOffset = (rowCounter % 2) * (blockL / 2);
    
    if (rowOffset > 0) {
      const firstBlockSize = Math.min(blockL/2, length/2 - currentX);
      if (firstBlockSize > 0.01) {
        blocks.push(
          <group key={`${currentY}-${currentX}-first`}>
            <mesh
              position={[currentX + firstBlockSize/2, currentY + rowHeight/2, 0]}
            >
              <boxGeometry args={[firstBlockSize, rowHeight, thickness]} />
              <meshStandardMaterial color="#b85a4a" />
            </mesh>
            <lineSegments position={[currentX + firstBlockSize/2, currentY + rowHeight/2, 0]}>
              <edgesGeometry args={[new THREE.BoxGeometry(firstBlockSize, rowHeight, thickness)]} />
              <lineBasicMaterial color="#111111" linewidth={2} />
            </lineSegments>
          </group>
        );
        currentX += firstBlockSize + mortarGap;
      }
    }
    
    while (currentX < length/2) {
      const remainingX = length/2 - currentX;
      const blockSizeX = Math.min(blockL, remainingX);
      
      if (blockSizeX > 0.01) {
        blocks.push(
          <group key={`${currentY}-${currentX}`}>
            <mesh
              position={[currentX + blockSizeX/2, currentY + rowHeight/2, 0]}
            >
              <boxGeometry args={[blockSizeX, rowHeight, thickness]} />
              <meshStandardMaterial color={rowCounter % 2 ? "#b85a4a" : "#a04a3a"} />
            </mesh>
            <lineSegments position={[currentX + blockSizeX/2, currentY + rowHeight/2, 0]}>
              <edgesGeometry args={[new THREE.BoxGeometry(blockSizeX, rowHeight, thickness)]} />
              <lineBasicMaterial color="#111111" linewidth={2} />
            </lineSegments>
          </group>
        );
        currentX += blockSizeX + mortarGap;
      } else {
        break;
      }
    }
    
    if (currentX < length/2 && rowOffset === 0) {
      const lastBlockSize = length/2 - currentX;
      if (lastBlockSize > 0.01) {
        blocks.push(
          <group key={`${currentY}-${currentX}-last`}>
            <mesh
              position={[currentX + lastBlockSize/2, currentY + rowHeight/2, 0]}
            >
              <boxGeometry args={[lastBlockSize, rowHeight, thickness]} />
              <meshStandardMaterial color="#b85a4a" />
            </mesh>
            <lineSegments position={[currentX + lastBlockSize/2, currentY + rowHeight/2, 0]}>
              <edgesGeometry args={[new THREE.BoxGeometry(lastBlockSize, rowHeight, thickness)]} />
              <lineBasicMaterial color="#111111" linewidth={2} />
            </lineSegments>
          </group>
        );
      }
    }
    
    currentY += rowHeight + mortarGap;
    rowCounter++;
  }

  return (
    <group>
      <group ref={lengthTextRef} position={[0, -height/2 - 0.25, 0]}>
        <Text color="black" anchorX="center" anchorY="middle" fontSize={0.1}>
          {length.toFixed(2)} m
        </Text>
      </group>
      
      <group ref={heightTextRef} position={[-length/2 - 0.25, 0, 0]}>
        <Text 
          color="black" 
          anchorX="center" 
          anchorY="middle" 
          fontSize={0.1}
          rotation={[0, 0, Math.PI/2]}
        >
          {height.toFixed(2)} m
        </Text>
      </group>
      
      {blocks}
    </group>
  );
};

const Main = () => 
{
  const [wallDimensions, setWallDimensions] = useState({
    length: 5,
    height: 2.5,
    thickness: 0.2
  });

  const [selectedBlock, setSelectedBlock] = useState(blockTypes[0]);
  const [results, setResults] = useState({
    fullBlocks: 0,
    cutBlocks: [],
    mortar: 0,
    totalWeight: 0,
    weightPerMeter: 0,
    verticalMortar: 0,
    horizontalMortar: 0
  });

  const calculateMaterials = () => {
    const { length, height, thickness } = wallDimensions;
    const { length: blockL, height: blockH, mortarGap, weight } = selectedBlock;

    let fullBlocks = 0;
    const cutBlocks = [];
    let currentY = 0;
    let rowCounter = 0;
    let totalMortarX = 0;
    let totalMortarY = 0;

    while (currentY < height) {
      const rowHeight = Math.min(blockH, height - currentY);
      const rowOffset = (rowCounter % 2) * (blockL / 2);
      let currentX = 0;
      let blocksInRow = 0;
      
      while (currentX < length) {
        const remainingX = length - currentX;
        let blockSizeX = blockL;
        
        if (blocksInRow === 0 && rowOffset > 0) {
          blockSizeX = Math.min(blockL/2, remainingX);
        } else {
          blockSizeX = Math.min(blockL, remainingX);
        }

        if (blockSizeX > 0.01) {
          if (Math.abs(blockSizeX - blockL) < 0.001 && Math.abs(rowHeight - blockH) < 0.001) {
            fullBlocks++;
          } else {
            cutBlocks.push({
              width: blockSizeX,
              height: rowHeight,
              length: thickness
            });
          }
          blocksInRow++;
          currentX += blockSizeX;
          
          if (currentX < length) {
            totalMortarX += thickness * rowHeight * mortarGap;
            currentX += mortarGap;
          }
        } else {
          break;
        }
      }
      
      currentY += rowHeight;
      
      if (currentY < height) {
        totalMortarY += length * thickness * mortarGap;
        currentY += mortarGap;
      }
      
      rowCounter++;
    }

    const verticalMortar = totalMortarX;
    const horizontalMortar = totalMortarY;
    const mortar = verticalMortar + horizontalMortar;

    const cutBlocksWeight = cutBlocks.reduce((sum, block) => sum + (block.width * block.height * block.length * weight / (blockL * blockH * thickness)), 0);
    const totalWeight = (fullBlocks * weight) + cutBlocksWeight + (mortar * mortarDensity * 1000);

    setResults({
      fullBlocks,
      cutBlocks,
      mortar,
      totalWeight,
      weightPerMeter: totalWeight / length,
      verticalMortar,
      horizontalMortar
    });
  };

  useEffect(() => {
    calculateMaterials();
  }, [wallDimensions, selectedBlock]);

  const handleDimensionChange = (e) => {
    const { name, value } = e.target;
    const numericValue = parseFloat(value) || 0;
    const roundedValue = Math.max(0.05, Math.round(numericValue * 20) / 20);
    setWallDimensions(prev => ({
      ...prev,
      [name]: roundedValue
    }));
  };

  const formatDimension = (value) => {
    const cmValue = Math.round(value * 100);
    return cmValue % 1 === 0 ? cmValue : cmValue.toFixed(1);
  };

  const renderCutBlocks = () => {
    const counts = {};
    results.cutBlocks.forEach(block => {
      const key = `${formatDimension(block.width)}x${formatDimension(block.height)}x${formatDimension(block.length)}`;
      counts[key] = (counts[key] || 0) + 1;
    });

    return Object.entries(counts).map(([size, count]) => (
      <p key={size} className="text-sm">
        <span className="font-semibold">Bloque {size} cm:</span> {count}
      </p>
    ));
  };

  // Código del sistema de menú
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const menuRef = useRef();

  const toggleMenu = (menuName) => {
    setOpenMenu(openMenu === menuName ? null : menuName);
  };

  const handleMenuAction = () => {
    setOpenMenu(null);
  };

  const handleAction = () => {
    setOpenMenu(null);
    // Aquí puedes añadir lógica adicional para cada acción
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenu(null);
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="flex flex-col h-screen">
      {/* Sponsors SOLO en responsive - Parte superior */}
      {isMobile && (
        <div className="bg-gray-50 p-1">
          <div className="flex justify-center items-center h-12">
            <span className="text-sm font-medium">Patrocinadores</span>
          </div>
        </div>
      )}

      {/* MENÚ SUPERIOR */}
      <div ref={menuRef} className="bg-gray-800 text-white flex justify-between items-center px-4 py-2 z-[3000]">
        <div className="flex space-x-4">
          
          {/* Menú Archivo - Botones de acción */}
          <div className="relative">
            <button 
              className="hover:bg-gray-700 px-3 py-1 rounded"
              onClick={() => toggleMenu('archivo')}
            >
              Archivo
            </button>

            <div className={`absolute left-0 mt-1 ${openMenu === 'archivo' ? 'block' : 'hidden'} bg-white text-black rounded shadow w-48`}>
              <button 
                className="block px-4 py-2 hover:bg-gray-100 w-full text-left"
                onClick={handleAction}
              >
                Nuevo
              </button>
              <button 
                className="block px-4 py-2 hover:bg-gray-100 w-full text-left"
                onClick={handleAction}
              >
                Abrir
              </button>
              <button 
                className="block px-4 py-2 hover:bg-gray-100 w-full text-left"
                onClick={handleAction}
              >
                Guardar
              </button>
            </div>
          </div>

          <button className="hover:bg-gray-700 px-3 py-1 rounded">Más Información</button>

          {/* Otras Calculadoras - Links de navegación */}
          <div className="relative">
            <button 
              className="hover:bg-gray-700 px-3 py-1 rounded cursor-pointer"
              onClick={() => toggleMenu('calculadoras')}
            >
              Otras Calculadoras
            </button>

            {/* Contenedor del menú desplegable - sin pointer-events */}
            <div 
              className={`absolute left-0 mt-1 ${openMenu === 'calculadoras' ? 'block' : 'hidden'} bg-white text-black rounded shadow w-[300px]`}
              style={{ pointerEvents: 'none' }} /* Deshabilita eventos en el contenedor */
            >
              {/* Contenido del menú - habilita eventos solo aquí */}
              <div style={{ pointerEvents: 'auto' }}>
                <Link 
                  to="/vigasrectangulareshormigon" 
                  className="block px-4 py-2 hover:bg-gray-100 w-full text-left cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenMenu(null);
                  }}
                >
                  Diseño De Vigas de Hormigón
                </Link>
                <Link 
                  to="/losasmacizashormigon" 
                  className="block px-4 py-2 hover:bg-gray-100 w-full text-left cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenMenu(null);
                  }}
                >
                  Diseño de Losas de Hormigón
                </Link>
                <Link 
                  to="/calculoparedes" 
                  className="block px-4 py-2 hover:bg-gray-100 w-full text-left cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenMenu(null);
                  }}
                >
                  Cálculo Paredes Ladrillo
                </Link>
                <Link 
                  to="/apus" 
                  className="block px-4 py-2 hover:bg-gray-100 w-full text-left cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenMenu(null);
                  }}
                >
                  Apus
                </Link>
              </div>
            </div>
          </div>
          
          {/* Créditos - Enlaces externos */}
          <div className="relative">
            <button 
              className="hover:bg-gray-700 px-3 py-1 rounded"
              onClick={() => toggleMenu('creditos')}
            >
              Créditos
            </button>
            <div className={`absolute left-0 mt-1 ${openMenu === 'creditos' ? 'block' : 'hidden'} bg-white text-black rounded shadow w-[300px]`}>
              <a 
                href="https://www.instagram.com/entornoceo/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block px-4 py-2 hover:bg-gray-100 w-full text-left cursor-pointer"
              >
                Diseño - Javier Hernández
              </a>
              <a 
                href="https://pmjarquitectos.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block px-4 py-2 hover:bg-gray-100 w-full text-left"
              >
                Pruebas - PMJ Arquitectos
              </a>
              <a 
                href="https://puceinvestiga.puce.edu.ec/es/organisations/facultad-de-ingenier%C3%ADa" 
                className="block px-4 py-2 hover:bg-gray-100 w-full text-left"
              >
                Revisión - PUCE (Gustavo Leoro)
              </a>
            </div>
          </div>

          <Link 
            to="/calculadoras"  
            className="hover:bg-gray-700 px-3 py-1 rounded text-white"
          >
            Regresar a Calculadoras
          </Link>  
        </div>
        <div>
          <span className="font-bold uppercase">Plantilla Para Calculadoras de Ingeniería</span>
        </div>
      </div>


      {/* CONTENIDO PRINCIPAL */}
      <div className={`flex ${isMobile ? 'flex-col-reverse' : 'overflow-hidden'} h-full`}>
        {/* Área de Gráficas / Modelo 3D */}
        <div className={`${isMobile ? 'flex-1' : 'flex flex-col flex-1'} bg-gray-50 p-1`}>
          <div className="h-full w-full">
            <Canvas>
              <ambientLight intensity={0.5} />
              <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} />
              <PerspectiveCamera makeDefault position={[5, 5, 5]} />
              <OrbitControls enablePan={true} enableZoom={true} enableRotate={true} />
              <Wall3D wall={wallDimensions} block={selectedBlock} />
            </Canvas>
          </div>

          <div className="flex flex-col bg-gray-50 p-1 flex-1">
            <SponsortsBar/>
          </div>
        </div>        

        {/* 🔴 Panel Derecho de Inputs */}
        <div className="bg-white border-l p-4 bg-gray-200 overflow-y-auto z-50 min-w-2/12 w-[300px]">          

          {/* Inputs de dimensiones */}
          <div className="space-y-4">
            {['length', 'height', 'thickness'].map((dim) => (
              <div key={dim}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {dim === 'length' ? 'Longitud' : dim === 'height' ? 'Altura' : 'Espesor'} (m)
                </label>
                <input
                  type="number"
                  name={dim}
                  value={wallDimensions[dim]}
                  onChange={handleDimensionChange}
                  className="block w-full rounded-md border-gray-300 shadow-sm p-2 border"
                  min="0.05"
                  step="0.05"
                />
                <p className="text-xs text-gray-500 mt-1">Incrementos de 5 cm (mínimo 5 cm)</p>
              </div>
            ))}

            {/* Selector de tipo de bloque */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de bloque/ladrillo</label>
              <select
                value={selectedBlock.id}
                onChange={(e) => setSelectedBlock(blockTypes.find(b => b.id === parseInt(e.target.value)))}
                className="block w-full rounded-md border-gray-300 shadow-sm p-2 border"
              >
                {blockTypes.map(block => (
                  <option key={block.id} value={block.id}>{block.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Resultados */}
          <div className="mt-6 p-4 bg-gray-100 rounded-lg">
            <h3 className="text-lg font-medium mb-3">Resultados</h3>
            <div className="space-y-2">
              <p><span className="font-semibold">Bloques completos:</span> {results.fullBlocks}</p>
              
              <div>
                <h4 className="font-semibold">Bloques cortados:</h4>
                {results.cutBlocks.length > 0 ? (
                  <div className="max-h-32 overflow-y-auto">
                    {renderCutBlocks()}
                  </div>
                ) : (
                  <p className="text-sm">No hay bloques cortados</p>
                )}
              </div>
              
              <div>
                <h4 className="font-semibold">Mortero:</h4>
                <p className="text-sm"><span className="font-semibold">Volumen total:</span> {results.mortar.toFixed(3)} m³</p>
                <p className="text-sm"><span className="font-semibold">Mortero vertical:</span> {results.verticalMortar?.toFixed(3) || '0.000'} m³</p>
                <p className="text-sm"><span className="font-semibold">Mortero horizontal:</span> {results.horizontalMortar?.toFixed(3) || '0.000'} m³</p>
              </div>
              
              <div>
                <h4 className="font-semibold">Pesos:</h4>
                <p className="text-sm"><span className="font-semibold">Peso total:</span> {results.totalWeight.toFixed(0)} kg</p>
                <p className="text-sm"><span className="font-semibold">Peso por metro lineal:</span> {results.weightPerMeter.toFixed(0)} kg/m</p>
              </div>
            </div>
          </div>
        </div>

        {/* Panel Derecho de Botones - Solo desktop */}
        {!isMobile && (
          <div className="flex flex-col items-center py-2 bg-gray-100 border-l w-16 overflow-y-auto space-y-2 z-50 min-w-1/12 w-[60px]">
            <Tooltip placement="left" title={"Configuración"}>
              <button className="w-10 h-10 bg-white shadow rounded flex items-center justify-center">
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6 1a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </button>
            </Tooltip>

            <Tooltip placement="left" title={"Grid"}>
              <button className="w-10 h-10 bg-white shadow rounded flex items-center justify-center">
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 2l9 4.5-9 4.5-9-4.5L12 2zm0 6.5l9 4.5-9 4.5-9-4.5 9-4.5zm0 6.5l9 4.5-9 4.5-9-4.5 9-4.5z"></path>
                </svg>
              </button>
            </Tooltip>

            <Tooltip placement="left" title={"Geometría"}>
              <button className="w-10 h-10 bg-white shadow rounded flex items-center justify-center">
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 2l9 4.5-9 4.5-9-4.5L12 2zm0 6.5l9 4.5-9 4.5-9-4.5 9-4.5zm0 6.5l9 4.5-9 4.5-9-4.5 9-4.5z"></path>
                </svg>
              </button>
            </Tooltip>

            <Tooltip placement="left" title={"Normativa"}>
              <button className="w-10 h-10 bg-white shadow rounded flex items-center justify-center">
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 2l9 4.5-9 4.5-9-4.5L12 2zm0 6.5l9 4.5-9 4.5-9-4.5 9-4.5zm0 6.5l9 4.5-9 4.5-9-4.5 9-4.5z"></path>
                </svg>
              </button>
            </Tooltip>

            <Tooltip placement="left" title={"Cargas"}>
              <button className="w-10 h-10 bg-white shadow rounded flex items-center justify-center">
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h18v18H3V3zm3 3h12v12H6V6z"></path>
                </svg>
              </button>
            </Tooltip>

            <Tooltip placement="left" title={"Elementos"}>
              <button className="w-10 h-10 bg-white shadow rounded flex items-center justify-center">
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12h14M12 5l7 7-7 7"></path>
                </svg>
              </button>
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
};

export default Main;